import UTIL from './util';
import { fixx } from './fixx';
import { initGsapScroll } from './gsap-scroll';

const MAIN = {
  isTouchDevice: typeof document !== 'undefined' && 'ontouchstart' in document.documentElement,
  _countHasRun: false,
  _countTimeoutId: null,
};

if (typeof window !== 'undefined') {
  window.MAIN = MAIN;
  window.UTIL = UTIL;
}

function bindScrollFrame(updateFn) {
  let running = false;
  let lastEventTs = 0;
  let lastFrameTs = 0;
  const idleMs = 400;
  let rafId = 0;

  function nowMs() {
    return window.performance && typeof performance.now === 'function' ? performance.now() : Date.now();
  }

  function loop(frameTs) {
    const dt = lastFrameTs ? Math.min(0.05, (frameTs - lastFrameTs) / 1000) : 0.016;
    lastFrameTs = frameTs;
    const keepGoing = updateFn(dt) === true;
    if (keepGoing || (frameTs - lastEventTs) < idleMs) {
      rafId = window.requestAnimationFrame(loop);
    } else {
      running = false;
      lastFrameTs = 0;
      updateFn(0);
    }
  }

  function kick() {
    lastEventTs = nowMs();
    if (!running) {
      running = true;
      lastFrameTs = 0;
      rafId = window.requestAnimationFrame(loop);
    }
  }

  window.addEventListener('scroll', kick, { passive: true });
  window.addEventListener('touchmove', kick, { passive: true });
  window.addEventListener('touchend', kick, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener('scroll', kick, { passive: true });
  }
  window.addEventListener('scrollend', kick);
  const unsubSettle = UTIL.onLayoutSettle(kick);
  updateFn(0);

  return function destroy() {
    window.removeEventListener('scroll', kick);
    window.removeEventListener('touchmove', kick);
    window.removeEventListener('touchend', kick);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('scroll', kick);
    }
    window.removeEventListener('scrollend', kick);
    unsubSettle();
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
  };
}

function approachValue(current, target, dt) {
  if (!Number.isFinite(current)) {
    return target;
  }
  if (dt <= 0) {
    return target;
  }
  const delta = target - current;
  const abs = Math.abs(delta);
  if (abs < 0.00001) {
    return target;
  }
  const rate = abs > 0.08 ? 48 : 16;
  return current + delta * (1 - Math.exp(-rate * dt));
}

function workItemCursorTip() {
  if (MAIN.isTouchDevice) {
    return function destroy() {};
  }
  if (window.matchMedia && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    return function destroy() {};
  }

  const work = document.getElementById('work');
  if (!work) {
    return function destroy() {};
  }

  const tip = document.createElement('div');
  tip.className = 'work-cursor-tip';
  tip.setAttribute('aria-hidden', 'true');
  tip.textContent = 'View';
  document.body.appendChild(tip);

  const offsetX = 13;
  const offsetY = 13;
  const showDelayMs = 200;
  let ease = 0.16;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    ease = 1;
  }

  let hovering = false;
  let visible = false;
  let showTimeoutId = null;
  let x = 0;
  let y = 0;
  let targetX = 0;
  let targetY = 0;
  let ticking = false;
  let rafId = 0;

  function setTransform() {
    tip.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }

  function tick() {
    const dx = targetX - x;
    const dy = targetY - y;
    if (Math.abs(dx) < 0.15 && Math.abs(dy) < 0.15) {
      x = targetX;
      y = targetY;
      ticking = false;
    } else {
      x += dx * ease;
      y += dy * ease;
      rafId = window.requestAnimationFrame(tick);
    }
    setTransform();
  }

  function requestTick() {
    if (!ticking) {
      ticking = true;
      rafId = window.requestAnimationFrame(tick);
    }
  }

  function pointFromEvent(e) {
    targetX = e.clientX + offsetX;
    targetY = e.clientY + offsetY;
  }

  function clearShowTimeout() {
    if (showTimeoutId) {
      window.clearTimeout(showTimeoutId);
      showTimeoutId = null;
    }
  }

  function reveal() {
    if (!hovering || visible) {
      return;
    }
    x = targetX;
    y = targetY;
    setTransform();
    tip.classList.add('is-visible');
    visible = true;
    requestTick();
  }

  function show(e) {
    pointFromEvent(e);
    hovering = true;
    if (visible) {
      requestTick();
      return;
    }
    if (showTimeoutId) {
      return;
    }
    showTimeoutId = window.setTimeout(function () {
      showTimeoutId = null;
      reveal();
    }, showDelayMs);
  }

  function hide() {
    hovering = false;
    visible = false;
    clearShowTimeout();
    tip.classList.remove('is-visible');
  }

  function onEnter(e) {
    const link = e.target.closest('.item a');
    if (!link || !work.contains(link)) {
      return;
    }
    show(e);
  }

  function onMove(e) {
    const link = e.target.closest('.item a');
    if (!link || !work.contains(link)) {
      return;
    }
    pointFromEvent(e);
    if (!hovering) {
      show(e);
      return;
    }
    if (visible) {
      requestTick();
    }
  }

  function onLeave(e) {
    const link = e.target.closest('.item a');
    if (!link || !work.contains(link)) {
      return;
    }
    if (e.relatedTarget && link.contains(e.relatedTarget)) {
      return;
    }
    hide();
  }

  work.addEventListener('mouseenter', onEnter, true);
  work.addEventListener('mousemove', onMove);
  work.addEventListener('mouseleave', onLeave, true);

  return function destroy() {
    work.removeEventListener('mouseenter', onEnter, true);
    work.removeEventListener('mousemove', onMove);
    work.removeEventListener('mouseleave', onLeave, true);
    clearShowTimeout();
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
    if (tip.parentNode) {
      tip.parentNode.removeChild(tip);
    }
  };
}

function clientLogosScrollReveal() {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return function destroy() {};
  }

  const grid = document.querySelector('.client-grid');
  if (!grid) {
    return function destroy() {};
  }

  const sectionEl = grid.closest('section');
  if (!sectionEl) {
    return function destroy() {};
  }

  const items = Array.from(grid.children).filter((el) => el.classList.contains('grid-item'));
  const n = items.length;
  if (!n) {
    return function destroy() {};
  }

  grid.classList.add('client-grid--scroll-reveal');

  const revealBlendSpanItems = 3;
  const itemRevealOffsetPx = 100;
  const itemStartBlurPx = 6;
  const itemStartOpacity = 0;
  const animationStartProgress = 0.2;
  const animationEndProgress = 0.7;

  function sectionProgress01() {
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const vh = UTIL.svh();
    const rect = sectionEl.getBoundingClientRect();
    const elHeight = sectionEl.offsetHeight;
    const track = elHeight + vh;
    if (track <= 0) {
      return 0;
    }
    const elTop = scrollY + rect.top;
    const startScroll = elTop - vh;
    let p = (scrollY - startScroll) / track;
    if (p < 0) {
      return 0;
    }
    if (p > 1) {
      return 1;
    }
    return p;
  }

  function smoothstep01(t) {
    if (t <= 0) {
      return 0;
    }
    if (t >= 1) {
      return 1;
    }
    return t * t * (3 - 2 * t);
  }

  function setItemRevealed(el, t) {
    const u = smoothstep01(t);
    const opacity = itemStartOpacity + ((1 - itemStartOpacity) * u);
    const blurPx = itemStartBlurPx * (1 - u);
    el.style.opacity = String(opacity);
    el.style.filter = blurPx > 0.01 ? `blur(${blurPx}px)` : 'none';
    el.style.transform = `translate3d(0, ${(1 - u) * itemRevealOffsetPx}px, 0)`;
  }

  function setItemFull(el) {
    el.style.opacity = '1';
    el.style.filter = 'none';
    el.style.transform = 'translate3d(0, 0, 0)';
  }

  function clearItem(el) {
    el.style.opacity = String(itemStartOpacity);
    el.style.filter = itemStartBlurPx > 0.01 ? `blur(${itemStartBlurPx}px)` : 'none';
    el.style.transform = `translate3d(0, ${itemRevealOffsetPx}px, 0)`;
  }

  function update() {
    const vh = UTIL.svh();
    if (vh <= 0) {
      return;
    }

    const rect = sectionEl.getBoundingClientRect();
    let i;

    if (rect.bottom < 0) {
      for (i = 0; i < n; i++) {
        setItemFull(items[i]);
      }
      return;
    }

    if (rect.top > vh) {
      for (i = 0; i < n; i++) {
        clearItem(items[i]);
      }
      return;
    }

    const p = sectionProgress01();
    let pNorm = (p - animationStartProgress) / (animationEndProgress - animationStartProgress);
    if (pNorm < 0) {
      pNorm = 0;
    }
    if (pNorm > 1) {
      pNorm = 1;
    }

    const revealHead = pNorm * (n - 1 + revealBlendSpanItems);

    for (i = 0; i < n; i++) {
      const raw = (revealHead - i) / revealBlendSpanItems;
      if (raw <= 0) {
        clearItem(items[i]);
      } else if (raw >= 1) {
        setItemFull(items[i]);
      } else {
        setItemRevealed(items[i], raw);
      }
    }
  }

  let ticking = false;
  let rafId = 0;
  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      rafId = window.requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }
  }

  update();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  return function destroy() {
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
  };
}

function projectsIntroSettings() {
  return {
    delay: 1,
    transitionSpan: 1.9,
    scrollUnitVh: 65,
    proxyMaxWidth: 2000,
    gridRevealStart: 0.2,
    gridRevealEnd: 0.92,
    gridRevealStartScale: 0.88,
    proxyFadeOutStart: 0,
    proxyFadeOutEnd: 0.22,
  };
}

function projectsIntroScrollPx(config) {
  const vh = Math.max(1, UTIL.scrollVh());
  const unitPx = Math.max(1, vh * ((config.scrollUnitVh || 65) / 100));
  return Math.max(0, (Math.max(0, config.delay || 0) + Math.max(0, config.transitionSpan || 0)) * unitPx);
}

function projectsScrollIntro() {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return function destroy() {};
  }

  const sectionEl = document.getElementById('projects');
  if (!sectionEl) {
    return function destroy() {};
  }
  const wrapperEl = document.getElementById('work') || sectionEl.parentElement;
  if (!wrapperEl) {
    return function destroy() {};
  }

  const gridEl = sectionEl.querySelector('.grid');
  if (!gridEl) {
    return function destroy() {};
  }

  const itemEls = Array.from(gridEl.children).filter((el) => el.classList.contains('item'));
  if (itemEls.length < 2) {
    return function destroy() {};
  }

  const firstEl = itemEls[0];
  const backgroundGridItems = itemEls.slice(1);
  const contentGridEl = sectionEl.querySelector('.content-grid');
  const contentScrollEl = sectionEl.querySelector('.content-scroll');
  const proxyHostEl = sectionEl.querySelector('.projects-intro-proxy-host') || sectionEl.querySelector('.container-limit') || sectionEl;
  const firstProxyEl = firstEl.cloneNode(true);
  firstProxyEl.classList.add('item--intro-proxy');
  firstProxyEl.setAttribute('aria-hidden', 'true');
  firstProxyEl.style.position = 'absolute';
  firstProxyEl.style.left = '0';
  firstProxyEl.style.top = '0';
  firstProxyEl.style.margin = '0';
  firstProxyEl.style.zIndex = '2';
  firstProxyEl.style.display = 'none';
  firstProxyEl.style.overflow = 'hidden';
  firstProxyEl.style.background = '#f4f4f4';
  firstProxyEl.style.willChange = 'left, top, width, height, border-radius';
  const proxyLinkEl = firstProxyEl.querySelector('a');
  if (proxyLinkEl) {
    proxyLinkEl.style.display = 'block';
    proxyLinkEl.style.width = '100%';
    proxyLinkEl.style.height = '100%';
    proxyLinkEl.setAttribute('tabindex', '-1');
  }
  const proxyFadeEl = firstProxyEl.querySelector('.hero-cover-wrapper .fade');
  proxyHostEl.appendChild(firstProxyEl);

  const config = projectsIntroSettings();
  const delay = Math.max(0, config.delay || 0);
  const transitionSpan = Math.max(0.0001, config.transitionSpan || 0);
  let gridRevealStart = clamp01(config.gridRevealStart || 0);
  let gridRevealEnd = clamp01(config.gridRevealEnd || 1);
  if (gridRevealEnd <= gridRevealStart) {
    gridRevealEnd = Math.min(1, gridRevealStart + 0.001);
  }
  let gridRevealStartScale = config.gridRevealStartScale || 0.88;
  if (gridRevealStartScale <= 0) {
    gridRevealStartScale = 0.01;
  }
  const proxyMaxWidth = Math.max(1, config.proxyMaxWidth || 2000);
  let proxyFadeOutStart = clamp01(config.proxyFadeOutStart || 0);
  let proxyFadeOutEnd = clamp01(config.proxyFadeOutEnd || 1);
  if (proxyFadeOutEnd <= proxyFadeOutStart) {
    proxyFadeOutEnd = Math.min(1, proxyFadeOutStart + 0.001);
  }

  const geom = {
    startLeft: 0,
    startTop: 0,
    startWidth: 0,
    startHeight: 0,
    endLeft: 0,
    endTop: 0,
    endWidth: 0,
    endHeight: 0,
    endRadiusPx: 0,
    ready: false,
  };

  function clamp01(v) {
    if (v <= 0) {
      return 0;
    }
    if (v >= 1) {
      return 1;
    }
    return v;
  }

  function easeInOut(t) {
    return t * t * (3 - 2 * t);
  }

  function sectionProgressRaw() {
    const vh = UTIL.scrollVh(wrapperEl);
    if (vh <= 0) {
      return 1;
    }
    const wrapperRect = wrapperEl.getBoundingClientRect();
    if (wrapperRect.top > 0) {
      return 0;
    }
    const unitPx = Math.max(1, vh * ((config.scrollUnitVh || 65) / 100));
    return (-wrapperRect.top) / unitPx;
  }

  function updateEndGeometry() {
    const firstRect = firstEl.getBoundingClientRect();
    const hostRect = proxyHostEl.getBoundingClientRect();
    if (firstRect.width <= 0 || firstRect.height <= 0) {
      return false;
    }
    geom.endLeft = firstRect.left - hostRect.left;
    geom.endTop = firstRect.top - hostRect.top;
    geom.endWidth = firstRect.width;
    geom.endHeight = firstRect.height;
    geom.endRadiusPx = parseFloat(window.getComputedStyle(firstEl).borderTopLeftRadius) || 0;
    return true;
  }

  function captureGeometry() {
    firstEl.style.transform = 'none';
    firstEl.style.zIndex = '2';
    firstEl.style.borderRadius = '';
    firstEl.style.visibility = '';
    firstProxyEl.style.display = 'none';

    if (!updateEndGeometry()) {
      geom.ready = false;
      return;
    }

    const vw = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 1);
    geom.startLeft = 0;
    geom.startTop = 0;
    geom.startWidth = Math.min(vw, proxyMaxWidth);
    geom.startHeight = UTIL.lvh();
    geom.ready = true;
  }

  function sectionProgress01() {
    const pRaw = sectionProgressRaw();
    return clamp01((pRaw - delay) / transitionSpan);
  }

  function render() {
    if (!geom.ready) {
      return;
    }
    if (!updateEndGeometry()) {
      return;
    }

    const transitionProgress = sectionProgress01();
    const eased = easeInOut(transitionProgress);
    const left = geom.startLeft + ((geom.endLeft - geom.startLeft) * eased);
    const top = geom.startTop + ((geom.endTop - geom.startTop) * eased);
    const width = geom.startWidth + ((geom.endWidth - geom.startWidth) * eased);
    const height = geom.startHeight + ((geom.endHeight - geom.startHeight) * eased);
    const radiusPx = geom.endRadiusPx * eased;
    const wrapperRect = wrapperEl.getBoundingClientRect();
    const fadeOutT = 1 - clamp01((transitionProgress - proxyFadeOutStart) / (proxyFadeOutEnd - proxyFadeOutStart));
    let contentScrollOpacity = contentScrollEl ? parseFloat(window.getComputedStyle(contentScrollEl).opacity) : 1;
    if (!Number.isFinite(contentScrollOpacity)) {
      contentScrollOpacity = 1;
    }
    contentScrollOpacity = clamp01(contentScrollOpacity);
    const proxyFadeOpacity = clamp01(contentScrollOpacity * fadeOutT);

    if (wrapperRect.top > 0) {
      firstProxyEl.style.display = 'block';
      firstProxyEl.style.left = `${geom.startLeft}px`;
      firstProxyEl.style.top = `${geom.startTop}px`;
      firstProxyEl.style.width = `${geom.startWidth}px`;
      firstProxyEl.style.height = `${geom.startHeight}px`;
      firstProxyEl.style.borderRadius = '0px';
      firstEl.style.visibility = 'hidden';
      firstEl.style.transform = 'none';
      firstEl.style.borderRadius = '';
      firstEl.style.zIndex = '1';
    } else if (eased >= 0.999) {
      firstProxyEl.style.display = 'none';
      firstEl.style.visibility = '';
      firstEl.style.transform = 'none';
      firstEl.style.borderRadius = '';
      firstEl.style.zIndex = '2';
    } else {
      firstProxyEl.style.display = 'block';
      firstProxyEl.style.left = `${left}px`;
      firstProxyEl.style.top = `${top}px`;
      firstProxyEl.style.width = `${width}px`;
      firstProxyEl.style.height = `${height}px`;
      firstProxyEl.style.borderRadius = `${radiusPx}px`;
      firstEl.style.visibility = 'hidden';
      firstEl.style.zIndex = '1';
    }
    if (proxyFadeEl) {
      proxyFadeEl.style.opacity = String(proxyFadeOpacity);
    }

    if (backgroundGridItems.length) {
      const revealT = clamp01((eased - gridRevealStart) / (gridRevealEnd - gridRevealStart));
      const revealEased = easeInOut(revealT);
      const bgScale = gridRevealStartScale + ((1 - gridRevealStartScale) * revealEased);
      let i;
      for (i = 0; i < backgroundGridItems.length; i++) {
        backgroundGridItems[i].style.opacity = String(revealEased);
        backgroundGridItems[i].style.transform = `scale(${bgScale})`;
        backgroundGridItems[i].style.transformOrigin = '50% 50%';
        backgroundGridItems[i].style.willChange = 'transform, opacity';
      }
      if (eased >= 0.999) {
        for (i = 0; i < backgroundGridItems.length; i++) {
          backgroundGridItems[i].style.opacity = '';
          backgroundGridItems[i].style.transform = '';
          backgroundGridItems[i].style.transformOrigin = '';
          backgroundGridItems[i].style.willChange = '';
        }
      }
    }

    if (contentGridEl) {
      contentGridEl.style.setProperty('--content-grid-padding', `${1 * eased}rem`);
      contentGridEl.style.padding = 'var(--content-grid-padding)';
    }
  }

  let ticking = false;
  let rafId = 0;
  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      rafId = window.requestAnimationFrame(function () {
        ticking = false;
        if (!geom.ready) {
          captureGeometry();
        }
        render();
      });
    }
  }

  function onResize() {
    captureGeometry();
    onScrollOrResize();
  }

  captureGeometry();
  render();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onResize);

  return function destroy() {
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onResize);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
    if (firstProxyEl.parentNode) {
      firstProxyEl.parentNode.removeChild(firstProxyEl);
    }
    firstEl.style.visibility = '';
    firstEl.style.transform = '';
    firstEl.style.borderRadius = '';
    firstEl.style.zIndex = '';
  };
}

function regionStateOnScroll() {
  const bodyEl = document.body;
  if (!bodyEl) {
    return function destroy() {};
  }

  const regions = Array.from(document.querySelectorAll('.region'));
  if (!regions.length) {
    return function destroy() {};
  }

  const regionStates = new WeakMap();
  const preparedVideos = new WeakSet();
  const wrapperVideoLocks = new WeakSet();
  let lastBg = null;
  let lastColor = null;
  let lastHighlight = null;
  let lastHighlightColor = null;
  let lastHeaderFade = null;
  let activeThemeRegionEl = null;

  function isThemeRegion(regionEl) {
    if (!regionEl || !regionEl.classList.contains('theme')) {
      return false;
    }
    return regionEl.hasAttribute('data-bg')
      || regionEl.hasAttribute('data-color')
      || regionEl.hasAttribute('data-highlight')
      || regionEl.hasAttribute('data-highlight-color');
  }

  function regionPreset(regionEl, attrName) {
    const raw = UTIL.attr(regionEl, attrName);
    if (raw == null || raw === '') {
      return null;
    }
    return String(raw).trim().toLowerCase();
  }

  function triggerYForPreset(preset, vh) {
    if (preset === 'complete') {
      return 0;
    }
    if (preset === 'late') {
      return vh * 0.25;
    }
    return vh * 0.75;
  }

  function triggerYForRegion(regionEl, vh) {
    return triggerYForPreset(regionPreset(regionEl, 'data-start') || 'early', vh);
  }

  function hasRegionEnded(regionEl, rect, vh) {
    const end = regionPreset(regionEl, 'data-end');
    if (!end) {
      return false;
    }
    return rect.bottom <= triggerYForPreset(end, vh);
  }

  function applyThemeFromRegion(regionEl) {
    let bg = UTIL.attr(regionEl, 'data-bg');
    let color = UTIL.attr(regionEl, 'data-color');
    let highlight = UTIL.attr(regionEl, 'data-highlight');
    let highlightColor = UTIL.attr(regionEl, 'data-highlight-color');

    bg = bg ? String(bg).trim() : '';
    color = color ? String(color).trim() : '';
    highlight = highlight ? String(highlight).trim() : '';
    highlightColor = highlightColor ? String(highlightColor).trim() : '';

    if (bg !== lastBg) {
      if (bg) {
        bodyEl.style.setProperty('--body-background', bg);
      } else {
        bodyEl.style.removeProperty('--body-background');
      }
      lastBg = bg;
    }

    if (color !== lastColor) {
      if (color) {
        bodyEl.style.setProperty('--body-color', color);
      } else {
        bodyEl.style.removeProperty('--body-color');
      }
      lastColor = color;
    }

    if (highlight !== lastHighlight) {
      if (highlight) {
        bodyEl.style.setProperty('--color-highlight', highlight);
      } else {
        bodyEl.style.removeProperty('--color-highlight');
      }
      lastHighlight = highlight;
    }

    if (highlightColor !== lastHighlightColor) {
      if (highlightColor) {
        bodyEl.style.setProperty('--color-highlight-color', highlightColor);
      } else {
        bodyEl.style.removeProperty('--color-highlight-color');
      }
      lastHighlightColor = highlightColor;
    }

    const headerFadeRaw = regionPreset(regionEl, 'data-header-fade');
    const headerFade = (headerFadeRaw !== 'false' && headerFadeRaw !== '0' && headerFadeRaw !== 'off') ? '1' : '0';
    if (headerFade !== lastHeaderFade) {
      bodyEl.style.setProperty('--header-fade-opacity', headerFade);
      lastHeaderFade = headerFade;
    }
  }

  function pickActiveThemeRegion(vh) {
    let crossedEl = null;
    let crossedTop = -Infinity;
    let bestEl = null;
    let bestDistance = Infinity;

    for (let i = 0; i < regions.length; i++) {
      const regionEl = regions[i];
      if (!isThemeRegion(regionEl)) {
        continue;
      }

      const rect = regionEl.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= vh) {
        continue;
      }
      if (hasRegionEnded(regionEl, rect, vh)) {
        continue;
      }

      const triggerY = triggerYForRegion(regionEl, vh);
      if (rect.top <= triggerY) {
        if (rect.top > crossedTop) {
          crossedTop = rect.top;
          crossedEl = regionEl;
        }
      }

      const distance = Math.abs(rect.top - triggerY);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestEl = regionEl;
      }
    }
    return crossedEl || bestEl;
  }

  function getRegionVideos(regionEl) {
    if (!regionEl) {
      return [];
    }
    return Array.from(regionEl.querySelectorAll('video')).filter((video) => !video.closest('.video-wrapper'));
  }

  function getWrapperVideos(regionEl) {
    if (!regionEl) {
      return [];
    }
    return Array.from(regionEl.querySelectorAll('.video-wrapper video'));
  }

  function getVideoPlaybackRate(videoEl) {
    const rateAttr = videoEl.getAttribute('playbackrate');
    if (!rateAttr) {
      return null;
    }
    const rate = parseFloat(String(rateAttr).trim());
    if (!Number.isFinite(rate) || rate <= 0) {
      return null;
    }
    return rate;
  }

  function applyVideoPlaybackRate(videoEl) {
    const rate = getVideoPlaybackRate(videoEl);
    if (rate === null) {
      return;
    }
    try {
      videoEl.defaultPlaybackRate = rate;
    } catch (e) {}
    try {
      videoEl.playbackRate = rate;
    } catch (e) {}
  }

  function prepareVideoPlaybackRate(videoEl) {
    if (preparedVideos.has(videoEl)) {
      return;
    }
    preparedVideos.add(videoEl);
    if (getVideoPlaybackRate(videoEl) === null) {
      return;
    }
    videoEl.addEventListener('loadedmetadata', function onMeta() {
      applyVideoPlaybackRate(videoEl);
    });
    videoEl.addEventListener('play', function onPlay() {
      applyVideoPlaybackRate(videoEl);
    });
    applyVideoPlaybackRate(videoEl);
  }

  function playVideo(videoEl) {
    applyVideoPlaybackRate(videoEl);
    const playPromise = videoEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {});
    }
  }

  function lockWrapperVideo(videoEl, regionEl) {
    if (wrapperVideoLocks.has(videoEl)) {
      return;
    }
    wrapperVideoLocks.add(videoEl);

    function pauseIfInactive() {
      if (!regionEl.classList.contains('region-active')) {
        videoEl.pause();
      }
    }

    videoEl.addEventListener('play', pauseIfInactive);
    videoEl.addEventListener('loadeddata', pauseIfInactive);
    pauseIfInactive();
  }

  function syncWrapperVideos(regionEl, isActive) {
    const videos = getWrapperVideos(regionEl);
    for (let j = 0; j < videos.length; j++) {
      prepareVideoPlaybackRate(videos[j]);
      lockWrapperVideo(videos[j], regionEl);
      if (isActive) {
        if (videos[j].paused) {
          playVideo(videos[j]);
        }
      } else if (!videos[j].paused) {
        videos[j].pause();
      }
    }
  }

  function syncRegionActiveClass(activeRegionEl) {
    for (let i = 0; i < regions.length; i++) {
      const regionEl = regions[i];
      const isActive = regionEl === activeRegionEl;
      regionEl.classList.toggle('region-active', isActive);
      syncWrapperVideos(regionEl, isActive);
    }
  }

  function triggerActiveCallback(regionEl) {
    if (!regionEl) {
      return;
    }
    let callbackPath = UTIL.attr(regionEl, 'data-active-callback');
    if (!callbackPath) {
      return;
    }
    callbackPath = String(callbackPath).trim();
    if (!callbackPath) {
      return;
    }
    UTIL.invokeFunctionByPath(callbackPath, [regionEl], window);
  }

  function updateRegions() {
    const vh = UTIL.svh() || document.documentElement.clientHeight || 1;

    for (let i = 0; i < regions.length; i++) {
      const regionEl = regions[i];
      const isVisible = UTIL.isElementVisible(regionEl, vh);
      const wasVisible = regionStates.get(regionEl);

      regionEl.classList.toggle('region-visible', isVisible);

      if (wasVisible !== isVisible) {
        regionStates.set(regionEl, isVisible);
        const videos = getRegionVideos(regionEl);
        for (let j = 0; j < videos.length; j++) {
          prepareVideoPlaybackRate(videos[j]);
          if (isVisible) {
            playVideo(videos[j]);
          } else {
            videos[j].pause();
          }
        }
      }
    }

    const nextActiveThemeRegionEl = pickActiveThemeRegion(vh);
    if (nextActiveThemeRegionEl === activeThemeRegionEl) {
      syncRegionActiveClass(activeThemeRegionEl);
      if (activeThemeRegionEl) {
        applyThemeFromRegion(activeThemeRegionEl);
      }
      return;
    }
    activeThemeRegionEl = nextActiveThemeRegionEl;
    syncRegionActiveClass(activeThemeRegionEl);
    if (activeThemeRegionEl) {
      applyThemeFromRegion(activeThemeRegionEl);
      triggerActiveCallback(activeThemeRegionEl);
    }
  }

  let ticking = false;
  let rafId = 0;
  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      rafId = window.requestAnimationFrame(function () {
        ticking = false;
        updateRegions();
      });
    }
  }
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
  updateRegions();

  return function destroy() {
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
    bodyEl.style.removeProperty('--body-background');
    bodyEl.style.removeProperty('--body-color');
    bodyEl.style.removeProperty('--color-highlight');
    bodyEl.style.removeProperty('--color-highlight-color');
    bodyEl.style.removeProperty('--header-fade-opacity');
  };
}

function contentVisibleOnScroll() {
  const els = Array.from(document.querySelectorAll('.content-visible'));
  if (!els.length) {
    return function destroy() {};
  }

  let lastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
  let scrollingDown = true;

  function isEdgeOnScreen(el, vh, useBottom) {
    const rect = el.getBoundingClientRect();
    const y = useBottom ? rect.bottom : rect.top;
    return y < vh && y >= 0;
  }

  function update() {
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (currentScrollY !== lastScrollY) {
      scrollingDown = currentScrollY > lastScrollY;
      lastScrollY = currentScrollY;
    }
    const vh = UTIL.svh() || document.documentElement.clientHeight || 1;
    const useBottom = !scrollingDown;
    for (let i = 0; i < els.length; i++) {
      els[i].classList.toggle('visible', isEdgeOnScreen(els[i], vh, useBottom));
    }
  }

  let ticking = false;
  let rafId = 0;
  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      rafId = window.requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }
  }

  update();
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);

  return function destroy() {
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
  };
}

function visibleCallbacksOnScroll() {
  const callbackEls = Array.from(document.querySelectorAll('[data-visible-callback]'));
  if (!callbackEls.length) {
    return function destroy() {};
  }

  const callbackStates = new WeakMap();

  function updateVisibleCallbacks() {
    const vh = UTIL.svh() || document.documentElement.clientHeight || 1;
    for (let i = 0; i < callbackEls.length; i++) {
      const el = callbackEls[i];
      const isVisible = UTIL.isElementVisible(el, vh);
      const wasVisible = callbackStates.get(el);
      if (wasVisible === isVisible) {
        continue;
      }
      callbackStates.set(el, isVisible);
      if (!isVisible) {
        continue;
      }

      let callbackPath = UTIL.attr(el, 'data-visible-callback');
      if (!callbackPath) {
        continue;
      }
      callbackPath = String(callbackPath).trim();
      if (!callbackPath) {
        continue;
      }
      UTIL.invokeFunctionByPath(callbackPath, [el], window);
    }
  }

  let ticking = false;
  let rafId = 0;
  function onScrollOrResize() {
    if (!ticking) {
      ticking = true;
      rafId = window.requestAnimationFrame(function () {
        ticking = false;
        updateVisibleCallbacks();
      });
    }
  }
  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize);
  updateVisibleCallbacks();

  return function destroy() {
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
  };
}

function bodyScrollDirectionClass() {
  const bodyEl = document.body;
  if (!bodyEl) {
    return function destroy() {};
  }

  let lastScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
  const directionThresholdPx = 40;
  let ticking = false;
  let rafId = 0;

  function updateDirectionClass() {
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const deltaY = currentScrollY - lastScrollY;

    if (Math.abs(deltaY) < directionThresholdPx) {
      ticking = false;
      return;
    }

    if (deltaY > 0) {
      bodyEl.classList.add('scroll-down');
      bodyEl.classList.remove('scroll-up');
    } else {
      bodyEl.classList.remove('scroll-down');
      bodyEl.classList.add('scroll-up');
    }

    if (currentScrollY <= 0) {
      bodyEl.classList.remove('scroll-down', 'scroll-up');
    }

    lastScrollY = currentScrollY;
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      rafId = window.requestAnimationFrame(updateDirectionClass);
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  return function destroy() {
    window.removeEventListener('scroll', onScroll);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
    bodyEl.classList.remove('scroll-down', 'scroll-up');
  };
}

function projectsPinArticle() {
  const sectionEl = document.getElementById('projects');
  if (!sectionEl) {
    return function destroy() {};
  }
  const wrapperEl = document.getElementById('work');
  if (!wrapperEl) {
    return function destroy() {};
  }

  const config = {
    offset: 0,
    startThreshold: 0,
    endThreshold: 0,
  };
  const introConfig = projectsIntroSettings();
  let endElement = wrapperEl.querySelector('.projects-pin-end');
  if (!endElement) {
    endElement = document.createElement('div');
    endElement.className = 'projects-pin-end';
    endElement.setAttribute('aria-hidden', 'true');
    wrapperEl.appendChild(endElement);
  }

  function pinFrameHeight() {
    const gridEl = sectionEl.querySelector('.content-grid');
    if (gridEl) {
      const gridH = gridEl.getBoundingClientRect().height;
      if (gridH > 0) {
        return Math.ceil(gridH);
      }
    }
    const raw = window.getComputedStyle(sectionEl).getPropertyValue('--project-items-min-height').trim();
    const vh = Math.max(1, UTIL.svh() || document.documentElement.clientHeight || 1);
    const match = raw.match(/^([\d.]+)(?:[sld])?vh$/i);
    if (match) {
      return Math.ceil((parseFloat(match[1]) / 100) * vh);
    }
    return vh;
  }

  function updatePinRunway() {
    const transitionScrollPx = projectsIntroScrollPx(introConfig);
    const framePx = pinFrameHeight();
    const markerTop = Math.ceil(transitionScrollPx + framePx);

    wrapperEl.style.minHeight = `${markerTop}px`;
    wrapperEl.style.position = 'relative';
    endElement.style.position = 'absolute';
    endElement.style.left = '0';
    endElement.style.top = `${markerTop}px`;
    endElement.style.width = '1px';
    endElement.style.height = '1px';
    endElement.style.pointerEvents = 'none';
  }

  updatePinRunway();
  const unsubSettle = UTIL.onLayoutSettle(updatePinRunway);
  const onLoad = () => updatePinRunway();
  window.addEventListener('load', onLoad);
  window.requestAnimationFrame(updatePinRunway);

  let runwayResizeObserver = null;
  const gridEl = sectionEl.querySelector('.content-grid');
  if (gridEl && typeof ResizeObserver === 'function') {
    runwayResizeObserver = new ResizeObserver(function () {
      updatePinRunway();
    });
    runwayResizeObserver.observe(gridEl);
  }

  const placeholder = wrapperEl.querySelector('.projects-pin-placeholder');
  const destroyFixx = fixx(sectionEl, {
    offset: config.offset,
    startElement: wrapperEl,
    startAt: 'top',
    startThreshold: config.startThreshold,
    endElement,
    endAt: 'top',
    endThreshold: config.endThreshold,
    fixedBreakpoints: ['xs', 'sm', 'md', 'lg'],
    placeholder: true,
    placeholderElement: placeholder || undefined,
    stateFixedClass: 'fixed',
    stateStaticClass: 'static',
    stateFreezeClass: 'freeze',
  });

  return function destroy() {
    destroyFixx();
    unsubSettle();
    window.removeEventListener('load', onLoad);
    if (runwayResizeObserver) {
      runwayResizeObserver.disconnect();
    }
    wrapperEl.style.minHeight = '';
    wrapperEl.style.position = '';
  };
}

function contentScrollHasPause(panel, section) {
  const pauseRaw = UTIL.attr(panel, 'data-intro-pause-for');
  if (pauseRaw == null || String(pauseRaw).trim() === '') {
    return false;
  }
  if (!(UTIL.lengthToVh(pauseRaw, panel) > 0)) {
    return false;
  }
  const pauseAtRaw = UTIL.attr(panel, 'data-intro-pause-at');
  const pauseAtNorm = pauseAtRaw == null ? '' : String(pauseAtRaw).trim().toLowerCase();
  if (pauseAtNorm !== '' && pauseAtNorm !== 'bottom') {
    return Number.isFinite(parseFloat(pauseAtNorm));
  }
  return section.querySelectorAll('.pause-placeholder').length > 0;
}

function contentScrollPausePin() {
  const wraps = Array.from(document.querySelectorAll('.content-scroll-pin-wrap'));
  const cleanups = [];

  wraps.forEach((wrapper) => {
    const section = wrapper.querySelector('section');
    const panel = section?.querySelector('.content-scroll');
    const endElement = wrapper.querySelector('.content-scroll-pin-end');
    if (!section || !panel || !endElement) {
      return;
    }
    if (section.id === 'projects') {
      return;
    }

    function motionSpanPx() {
      const vh = Math.max(1, UTIL.scrollVh(panel));
      let spanVh = UTIL.attrNumber(panel, 'data-intro-motion-span-vh');
      if (spanVh == null || spanVh <= 0) {
        spanVh = 100;
      }
      let pauseVh = UTIL.lengthToVh(UTIL.attr(panel, 'data-intro-pause-for') || '0', panel);
      if (!Number.isFinite(pauseVh) || pauseVh < 0) {
        pauseVh = 0;
      }
      return (spanVh / 100) * vh + (pauseVh / 100) * vh;
    }

    function updatePinRunway() {
      const transitionScrollPx = motionSpanPx();
      const sectionHeight = Math.max(1, UTIL.lvh());
      const markerTop = Math.ceil(transitionScrollPx + sectionHeight);
      wrapper.style.minHeight = `${markerTop}px`;
      wrapper.style.position = 'relative';
      endElement.style.position = 'absolute';
      endElement.style.left = '0';
      endElement.style.top = `${markerTop}px`;
      endElement.style.width = '1px';
      endElement.style.height = '1px';
      endElement.style.pointerEvents = 'none';
    }

    updatePinRunway();
    const unsubSettle = UTIL.onLayoutSettle(updatePinRunway);
    const placeholder = wrapper.querySelector('.fixx-placeholder');
    const destroyFixx = fixx(section, {
      offset: 0,
      startElement: wrapper,
      startAt: 'top',
      startThreshold: 0,
      endElement,
      endAt: 'top',
      endThreshold: 0,
      fixedBreakpoints: ['xs', 'sm', 'md', 'lg'],
      placeholder: true,
      placeholderElement: placeholder || undefined,
      leftPositioning: true,
      stateFixedClass: 'fixed',
      stateStaticClass: 'static',
      stateFreezeClass: 'freeze',
    });

    cleanups.push(function destroy() {
      destroyFixx();
      unsubSettle();
      wrapper.style.minHeight = '';
      wrapper.style.position = '';
    });
  });

  return function destroy() {
    cleanups.forEach((fn) => fn());
  };
}

function swing(p) {
  return 0.5 - Math.cos(p * Math.PI) / 2;
}

function startCountAnimation(countEls, durationMs) {
  MAIN._countHasRun = true;
  countEls.forEach((el) => {
    const target = parseInt(el.getAttribute('data-count-target'), 10) || 0;
    const start = performance.now();
    el.textContent = '0';
    function frame(now) {
      const t = Math.min(1, (now - start) / durationMs);
      el.textContent = String(Math.ceil(target * swing(t)));
      if (t < 1) {
        window.requestAnimationFrame(frame);
      } else {
        el.textContent = String(target);
      }
    }
    window.requestAnimationFrame(frame);
  });
}

MAIN.count = function () {
  const stats = document.getElementById('stats');
  if (!stats) {
    return;
  }

  const countEls = Array.from(stats.querySelectorAll('.count'));
  const delayMs = 0;
  const durationMs = 2000;

  function clearPending() {
    if (MAIN._countTimeoutId) {
      window.clearTimeout(MAIN._countTimeoutId);
      MAIN._countTimeoutId = null;
    }
  }

  if (MAIN._countHasRun) {
    clearPending();
    return;
  }

  function maybeStart() {
    const isVisible = UTIL.isElementVisible(stats, UTIL.svh());
    if (!isVisible) {
      return;
    }
    clearPending();
    MAIN._countTimeoutId = window.setTimeout(function () {
      MAIN._countTimeoutId = null;
      if (MAIN._countHasRun) {
        return;
      }
      startCountAnimation(countEls, durationMs);
    }, delayMs);
  }

  clearPending();
  window.addEventListener('scroll', maybeStart, { passive: true });
  maybeStart();
};

function galleryImagePan() {
  const targets = [];

  function addTargets(rootSelector, frameSelector) {
    const roots = document.querySelectorAll(rootSelector);
    for (let i = 0; i < roots.length; i++) {
      const root = roots[i];
      const imgs = root.querySelectorAll('img');
      for (let j = 0; j < imgs.length; j++) {
        const img = imgs[j];
        targets.push({
          img,
          frame: img.closest(frameSelector) || root,
          host: root.closest('[data-scroll-y-start-vh], [data-scroll-start]') || root.closest('section'),
        });
      }
    }
  }

  addTargets('.gallery', '.item');
  addTargets('.animate-screen', '.screen');
  if (!targets.length) {
    return function destroy() {};
  }

  function clamp01(v) {
    if (v <= 0) {
      return 0;
    }
    if (v >= 1) {
      return 1;
    }
    return v;
  }

  function isReverse(host) {
    const reverseRaw = UTIL.attr(host, 'data-scroll-y-reverse');
    if (reverseRaw == null) {
      return false;
    }
    const reverseNorm = String(reverseRaw).trim().toLowerCase();
    return reverseNorm === '' || reverseNorm === 'true' || reverseNorm === '1' || reverseNorm === 'reverse';
  }

  function scrollProgress(host) {
    if (!host) {
      return 0;
    }
    const value = parseFloat(host.style.getPropertyValue('--scroll-y'));
    if (Number.isFinite(value)) {
      return clamp01(isReverse(host) ? ((100 - value) / 100) : (value / 100));
    }
    let top = host.getBoundingClientRect().top;
    if (window.getComputedStyle(host).position === 'fixed' && host.parentElement) {
      top = host.parentElement.getBoundingClientRect().top;
    }
    if (top > 0) {
      return 0;
    }
    return clamp01((-top) / Math.max(1, host.offsetHeight));
  }

  function frameInnerHeight(frame) {
    const style = window.getComputedStyle(frame);
    const pad = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    return Math.max(0, frame.clientHeight - pad);
  }

  function update(dt) {
    let keepGoing = false;
    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];
      const progress = scrollProgress(target.host);
      const travel = Math.max(0, target.img.offsetHeight - frameInnerHeight(target.frame));
      target.displayed = approachValue(target.displayed, progress, dt);
      if (Math.abs((target.displayed || 0) - progress) > 0.0004) {
        keepGoing = true;
      } else {
        target.displayed = progress;
      }
      target.img.style.transform = `translate3d(0, ${-travel * target.displayed}px, 0)`;
      target.img.style.willChange = 'transform';
    }
    return keepGoing;
  }

  const onLoadHandlers = [];
  for (let k = 0; k < targets.length; k++) {
    if (!targets[k].img.complete) {
      const handler = () => update(0);
      targets[k].img.addEventListener('load', handler);
      onLoadHandlers.push([targets[k].img, handler]);
    }
  }

  const unbindScroll = bindScrollFrame(update);

  return function destroy() {
    unbindScroll();
    onLoadHandlers.forEach(([img, handler]) => img.removeEventListener('load', handler));
  };
}

function sectionUrlSync() {
  const links = Array.from(document.querySelectorAll('#nav-primary a'));
  if (!links.length) {
    return function destroy() {};
  }

  function getSectionIdFromHref(href) {
    if (!href) {
      return null;
    }
    if (href.trim() === '#') {
      return null;
    }
    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return null;
    }
    if (url.hash.length > 1) {
      return url.hash.replace(/^#/, '').trim();
    }
    const qsSection = url.searchParams.get('section');
    if (qsSection) {
      return qsSection.replace(/^#/, '').trim();
    }
    return null;
  }

  const navSectionIds = links.map((link) => getSectionIdFromHref(link.getAttribute('href'))).filter(Boolean);
  const sections = navSectionIds.map((id) => {
    const el = document.getElementById(id);
    return el ? { id, el } : null;
  }).filter(Boolean);

  if (!sections.length) {
    return function destroy() {};
  }

  let currentId = null;

  function replaceQueryParam(key, value) {
    try {
      const url = new URL(window.location.href);
      if (!value) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
      url.hash = '';
      window.history.replaceState({}, document.title, url.toString());
    } catch (err) {}
  }

  function updateUrl(nextId) {
    if (nextId === currentId) {
      return;
    }
    currentId = nextId;
    replaceQueryParam('section', nextId);
  }

  let ticking = false;
  let rafId = 0;
  function handleScrollOrResize() {
    if (ticking) {
      return;
    }
    ticking = true;
    rafId = window.requestAnimationFrame(function () {
      const viewportHeight = UTIL.svh() || document.documentElement.clientHeight || 0;
      const best = { id: null, ratio: 0 };

      sections.forEach((section) => {
        const rect = section.el.getBoundingClientRect();
        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, viewportHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const ratio = viewportHeight ? (visibleHeight / viewportHeight) : 0;
        if (ratio > best.ratio) {
          best.id = section.id;
          best.ratio = ratio;
        }
      });

      const enterRatio = 0.32;
      const exitRatio = 0.18;
      const switchDelta = 0.05;

      function ratioFor(id) {
        const found = sections.find((s) => s.id === id);
        if (!found) {
          return 0;
        }
        const rect = found.el.getBoundingClientRect();
        const vt = Math.max(rect.top, 0);
        const vb = Math.min(rect.bottom, viewportHeight);
        const vh = Math.max(0, vb - vt);
        return viewportHeight ? (vh / viewportHeight) : 0;
      }

      const currentRatio = currentId ? ratioFor(currentId) : 0;
      const bestIsNav = navSectionIds.indexOf(best.id) !== -1;

      if (!currentId) {
        if (bestIsNav && best.ratio >= enterRatio) {
          updateUrl(best.id);
        } else {
          updateUrl(null);
        }
      } else if (best.id === currentId) {
        if (currentRatio >= exitRatio) {
          updateUrl(currentId);
        } else {
          updateUrl(null);
        }
      } else {
        const shouldSwitch = bestIsNav && best.ratio >= enterRatio && best.ratio >= (currentRatio + switchDelta);
        if (shouldSwitch) {
          updateUrl(best.id);
        } else if (currentRatio >= exitRatio) {
          updateUrl(currentId);
        } else {
          updateUrl(null);
        }
      }
      ticking = false;
    });
  }

  window.addEventListener('scroll', handleScrollOrResize, { passive: true });
  window.addEventListener('resize', handleScrollOrResize);
  handleScrollOrResize();

  return function destroy() {
    window.removeEventListener('scroll', handleScrollOrResize);
    window.removeEventListener('resize', handleScrollOrResize);
    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }
  };
}

export function initPageEffects() {
  const cleanups = [
    workItemCursorTip(),
    clientLogosScrollReveal(),
    projectsPinArticle(),
    contentScrollPausePin(),
    galleryImagePan(),
    projectsScrollIntro(),
    regionStateOnScroll(),
    contentVisibleOnScroll(),
    visibleCallbacksOnScroll(),
    bodyScrollDirectionClass(),
    sectionUrlSync(),
    initGsapScroll(),
  ];

  return function destroy() {
    cleanups.forEach((fn) => {
      if (typeof fn === 'function') {
        fn();
      }
    });
  };
}

export { MAIN };
